import { NavigationUser } from '../../interface/navigation.interface';
import { shouldShowMunilyPayMenuItem } from '../constants/financial-menu-items.constants';
import { ROUTES, mainFeatures } from './menu-items';
import { RouteInfo } from './sidebar.metadata';

/**
 * VDS-3315 — data + gate tables extracted from `SidebarComponent`'s `buildMenu()` dispatch
 * so `NavigationService.resolvePostSwitchRoute()` can answer "is this menu key reachable for
 * user+building" synchronously, without duplicating the sidebar's own menu-building logic.
 *
 * Fail-open posture: an unknown key, unknown gate, or missing table entry MUST resolve to
 * "available" (today's behaviour) — never a wrong denial. See design.md D1.
 */

export type MenuGate = (building: any, user: NavigationUser) => boolean;

interface MenuPathEntry {
  key: string;
  path: string;
}

/** Only internal, non-external-link menu paths are preservable landing routes. */
function buildMenuPaths(): Record<string, string> {
  const paths: Record<string, string> = {};
  for (const key of Object.keys(ROUTES)) {
    const route: RouteInfo = ROUTES[key];
    if (route?.path && route.path.startsWith('/') && !route.externalLink) {
      paths[key] = route.path;
    }
  }
  return paths;
}

/** Key -> parameter-free menu path. Excludes `path: ''` dropdown shells and external links. */
export const MENU_PATHS: Readonly<Record<string, string>> = buildMenuPaths();

function buildMenuPathIndex(): readonly MenuPathEntry[] {
  return Object.entries(MENU_PATHS)
    .map(([key, path]) => ({ key, path }))
    // Longest path first so the deepest menu prefix wins; lexicographic as a deterministic tiebreaker.
    .sort((a, b) => b.path.length - a.path.length || a.path.localeCompare(b.path));
}

/** Built once at module load — same pattern as `PROPRIETOR_ALLOWED_PATHS`. */
const MENU_PATH_INDEX: readonly MenuPathEntry[] = buildMenuPathIndex();

/** Longest menu-path prefix matching `path` on a segment boundary, or `null` if none. */
export function resolveMenuKeyForPath(path: string): string | null {
  const entry = MENU_PATH_INDEX.find((candidate) => path === candidate.path || path.startsWith(`${candidate.path}/`));
  return entry ? entry.key : null;
}

/**
 * The 10 `/app/*` prefixes whose `canActivate` includes `SuperAdminRol` in `pages.routing.ts`.
 * Anti-rot: `menu-availability.spec.ts` asserts this stays in sync with `PagesRouting`.
 */
export const SUPER_ADMIN_GUARDED_PREFIXES: readonly string[] = [
  '/app/buildings',
  '/app/dashboard-admin',
  '/app/communities',
  '/app/user-administration',
  '/app/app-services-super',
  '/app/settings',
  '/app/app-notice-munily-super',
  '/app/maia',
  '/app/legal-policies',
  '/app/munily-pay',
];

/** Segment-boundary match against `SUPER_ADMIN_GUARDED_PREFIXES`. */
export function isSuperAdminGuardedPath(path: string): boolean {
  return SUPER_ADMIN_GUARDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/**
 * Mirror of `buildTraditionalAdminMenu()` (`sidebar.component.ts`). Deliberately incomplete:
 * `accountStatements` is omitted (its gate reads `generalConfig.feature.contability` over HTTP,
 * unreachable from a synchronous predicate) — a missing entry here must fail OPEN, not deny.
 */
export const ADMIN_CONDITIONAL_MENU_GATES: Readonly<Record<string, MenuGate>> = {
  [mainFeatures.chats]: (building) => !!building?.chatV2?.enable,
  [mainFeatures.ticketsV2]: (building) => !!building?.pqrs?.enable,
  [mainFeatures.munilyPayTransactions]: (building, user) => shouldShowMunilyPayMenuItem(user, building?.fintech),
  [mainFeatures.doorOpeningAdmin]: (building) =>
    !!(building?.accessControl?.preRegisterVisit?.enable || building?.accessControl?.vigilantVisit?.enable),
  [mainFeatures.vigilantCalls]: (building) => !!(building?.directory?.enable || building?.invitation?.directCallVigilant),
  [mainFeatures.recordLog]: (building) => !!(building?.recordLog?.enable && building?.recordLog?.boardLink),
  [mainFeatures.roundControl]: (building) => !!(building?.roundControl?.enable && building?.roundControl?.boardLink),
  [mainFeatures.shiftControl]: (building) => !!(building?.shiftControl?.enable && building?.shiftControl?.boardLink),
};

/**
 * Moved verbatim from `buildWhiteLabelMenu()` (`sidebar.component.ts:161-185`) — single source
 * of truth for both the sidebar's menu build and the pre-switch availability predicate.
 */
export const WHITE_LABEL_FEATURE_MAPPING: Readonly<Record<string, string>> = {
  // Items principales
  notices: mainFeatures.notices,
  emergencies: mainFeatures.emergency,
  pqrs: mainFeatures.ticketsV2,
  ticketsV2: mainFeatures.ticketsV2,
  reservations: mainFeatures.reservations,
  visits: mainFeatures.visits,
  frequentAccess: mainFeatures.frequentAccess,
  // Sub-items de administrativeDropdown
  users: mainFeatures.users,
  tenants: mainFeatures.users,
  registrations: mainFeatures.registrations,
  administration: mainFeatures.administration,
  areas: mainFeatures.areas,
  buildingsAdmin: mainFeatures.buildingsAdmin,
  // Macro community features
  noticesMacro: mainFeatures.noticesMacro,
  reservationsMacro: mainFeatures.reservationsMacro,
  usersMacro: mainFeatures.usersMacro,
  areasMacro: mainFeatures.areasMacro,
  // Dropdowns completos (si se quiere incluir todo el dropdown)
  administrativeDropdown: mainFeatures.administrativeDropdown,
  administrativeMacroDropdown: mainFeatures.administrativeMacroDropdown,
};

/** Moved verbatim from `buildWhiteLabelMenu()` (`sidebar.component.ts:188-197`). */
export const WHITE_LABEL_DROPDOWN_SUB_ITEMS: Readonly<Record<string, string[]>> = {
  [mainFeatures.administrativeDropdown]: [
    mainFeatures.users,
    mainFeatures.registrations,
    mainFeatures.administration,
    mainFeatures.areas,
    mainFeatures.buildingsAdmin,
  ],
  [mainFeatures.administrativeMacroDropdown]: [mainFeatures.usersMacro, mainFeatures.areasMacro],
};

/** White-label `showFeatures` set, expanded with mapped dropdowns + the `pqrs` ⇒ `ticketsV2` rule. */
function resolveWhiteLabelMenuKeys(showFeatures: string[]): string[] {
  const withTicketsV2 =
    showFeatures.includes('pqrs') && !showFeatures.includes('ticketsV2') ? [...showFeatures, 'ticketsV2'] : showFeatures;
  const mapped = withTicketsV2.map((feature) => WHITE_LABEL_FEATURE_MAPPING[feature] || feature);
  const expanded = new Set(mapped);
  for (const [dropdown, subItems] of Object.entries(WHITE_LABEL_DROPDOWN_SUB_ITEMS)) {
    if (mapped.includes(dropdown)) {
      subItems.forEach((subItem) => expanded.add(subItem));
    }
  }
  return [...expanded];
}

/** Branch (d) of the resolution table: is `key` reachable from the white-label `showFeatures` set. */
export function isMenuKeyInWhiteLabelShowFeatures(key: string, showFeatures: string[]): boolean {
  return resolveWhiteLabelMenuKeys(showFeatures).includes(key);
}
